import { useState, useMemo } from "react";
import { RxType, RxFileFormat, Platform, ConnectionType } from "@/types/enums";
import { Rx } from "@/types/rx";
import { User } from "@/types/user";
import {
    UserConnection,
    FieldConnection,
    IrrigationSystemConnection,
} from "@/types/connection";
import {
    getIrrigationSystemDisplayName,
    IrrigationSystem,
} from "@/types/irrigationSystem";
import { ControllerBrand } from "@/types/pump";
import { normalizeIrrigationSystemIds } from "@/lib/utils/irrigation";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import {
    Button,
    Checkbox,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Autocomplete,
    AutocompleteItem,
    useDisclosure,
    Link,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDownload,
    faFileExport,
    faFileZipper,
    faFilePdf,
    faArrowUpRightFromSquare,
    faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import NewConnection from "../field/NewConnection";
import { useTranslation } from "react-i18next";

type ExportTarget = {
    connectionId: number;
    irrigationSystemId?: number;
};

type ExportOption = {
    key: string;
    label: string;
    type: ConnectionType | "more";
    platform: Platform | null;
    connectionId?: number;
    userConnectionId?: number;
};

type ExportIrrigationSystem = Pick<IrrigationSystem, "id" | "name">;

function isIrrigationSystem(
    system: ExportIrrigationSystem | IrrigationSystem | undefined,
): system is IrrigationSystem {
    return !!system && "pump" in system;
}

function RxExport({ rx, user }: { rx: Rx; user?: User }) {
    const { t } = useTranslation();
    const { userIsSentinel } = useAuth();
    const { downloadFile, useGetRequest, useMutateRequest } = useRequest();
    const { fieldQuery } = useFieldDashboard() ?? {};
    const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [formatModalOpen, setFormatModalOpen] = useState<boolean>(false);
    const [fileFormat, setFileFormat] = useState<RxFileFormat | null>(null);
    const fileFormatOptions = useMemo(() => {
        return rx.rx_type === RxType.ZONE
            ? [
                  {
                      key: RxFileFormat.JohnDeereMonitor,
                      label: "John Deere Monitor",
                  },
                  {
                      key: RxFileFormat.JohnDeereOpsCenter,
                      label: "John Deere Operations Center",
                  },
                  {
                      key: RxFileFormat.ValleyVRI,
                      label: "Valley VRI",
                  },
              ]
            : rx.rx_type === RxType.VRI
              ? [
                    {
                        key: RxFileFormat.FieldNET,
                        label: RxFileFormat.FieldNET,
                    },
                    {
                        key: RxFileFormat.FieldWISE,
                        label: RxFileFormat.FieldWISE,
                    },
                    {
                        key: RxFileFormat.AgSenseCommanderVP,
                        label: RxFileFormat.AgSenseCommanderVP,
                    },
                    {
                        key: RxFileFormat.AgSenseFieldCommander,
                        label: RxFileFormat.AgSenseFieldCommander,
                    },
                    {
                        key: RxFileFormat.AgSenseIconLink,
                        label: RxFileFormat.AgSenseIconLink,
                    },
                ]
              : [];
    }, [rx.rx_type]);
    const handleExportError = (error: unknown) => {
        if (error instanceof Error) {
            const resp = JSON.parse(error.message);
            setWarnings(resp?.warnings);
            setErrors(resp?.errors);
        }
        setWarningModalOpen(true);
    };
    const tryDownload = async (url: string, fileName = rx.name) => {
        try {
            await downloadFile(url, fileName);
        } catch (error) {
            handleExportError(error);
        } finally {
            setFormatModalOpen(false);
        }
    };
    const irrigationSystems = useMemo(() => {
        const fieldSystems = fieldQuery?.data?.irrigation_systems ?? [];
        const associatedIds = normalizeIrrigationSystemIds(
            rx.irrigation_systems,
        );
        if (!fieldSystems.length) {
            return associatedIds.map((id) => ({ id, name: "" }));
        }
        if (!associatedIds.length) return fieldSystems;
        const matched = fieldSystems.filter((system) =>
            associatedIds.includes(system.id),
        );
        return matched.length ? matched : fieldSystems;
    }, [fieldQuery?.data?.irrigation_systems, rx.irrigation_systems]);
    const isFertigationRx =
        rx.rx_type === RxType.VRI || rx.rx_type === RxType.VRF;
    const needsSystemPicker = isFertigationRx && irrigationSystems.length > 1;
    const userConnections = useGetRequest<UserConnection[]>({
        url: `/user_connections/`,
        params: {
            user: user?.id,
        },
        enabled: !!user,
    });
    const fieldConnections = useGetRequest<FieldConnection[]>({
        url: `/field_connections/`,
        params: {
            field: rx.field,
        },
        enabled: !!rx.field,
    });
    const irrigationSystemConnections = useGetRequest<
        IrrigationSystemConnection[]
    >({
        url: `/irrigation_system_connections/`,
        params: {
            irrigation_system: irrigationSystems.map((system) => system.id).join(","),
        },
        enabled: isFertigationRx && irrigationSystems.length > 0,
    });
    const platformFilter: Platform[] = useMemo(() => {
        return rx.rx_type === RxType.ZONE
            ? [Platform.FieldView]
            : [Platform.FieldNET];
    }, [rx.rx_type]);
    const exportOptions = useMemo(() => {
        let options: ExportOption[] = [];
        if (rx.rx_type === RxType.ZONE) {
            if (userConnections.data) {
                options = options.concat(
                    userConnections.data
                        .filter((connection) => {
                            return connection.platform === Platform.JohnDeere;
                        })
                        .map((connection) => {
                            return {
                                key: `user-${connection.id}`,
                                label: `My ${connection.platform}`,
                                type: ConnectionType.User,
                                platform: connection.platform,
                                connectionId: connection.id,
                            };
                        }),
                );
            }
            if (fieldConnections.data) {
                options = options.concat(
                    fieldConnections.data
                        .filter((connection) => {
                            return platformFilter.includes(
                                connection.user_connection.platform,
                            );
                        })
                        .map((connection) => {
                            return {
                                key: `field-${connection.id}`,
                                label: `${connection.user_connection.platform} Connected Field`,
                                type: ConnectionType.Field,
                                platform: connection.user_connection.platform,
                                connectionId: connection.id,
                            };
                        }),
                );
            }
        } else if (irrigationSystemConnections.data) {
            const connections = irrigationSystemConnections.data.filter(
                (connection) => {
                    return platformFilter.includes(
                        connection.user_connection.platform,
                    );
                },
            );
            if (needsSystemPicker) {
                const seenUserConnections = new Set<number>();
                connections.forEach((connection) => {
                    const userConnectionId = connection.user_connection.id;
                    if (seenUserConnections.has(userConnectionId)) return;
                    seenUserConnections.add(userConnectionId);
                    options.push({
                        key: `user-connection-${userConnectionId}`,
                        label: `${connection.user_connection.user === user?.id ? "My" : connection.user_connection.username} ${connection.user_connection.platform} (Connected)`,
                        type: ConnectionType.IrrigationSystem,
                        platform: connection.user_connection.platform,
                        userConnectionId,
                    });
                });
            } else {
                options = options.concat(
                    connections.map((connection) => {
                        return {
                            key: `irrigation-${connection.id}`,
                            label: `${connection.user_connection.user === user?.id ? "My" : connection.user_connection.username} ${connection.user_connection.platform} (Connected)`,
                            type: ConnectionType.IrrigationSystem,
                            platform: connection.user_connection.platform,
                            connectionId: connection.id,
                        };
                    }),
                );
            }
        }
        if (userIsSentinel) {
            options = options.concat([
                {
                    key: "more",
                    label: "More Connections...",
                    type: "more",
                    platform: null,
                },
            ]);
        }
        return options;
    }, [
        userConnections.data,
        fieldConnections.data,
        irrigationSystemConnections.data,
        rx.rx_type,
        user?.id,
        userIsSentinel,
        platformFilter,
        needsSystemPicker,
    ]);
    const [action, setAction] = useState<"download" | "export">("download");
    const [connectionId, setConnectionId] = useState<number>(0);
    const [connectionType, setConnectionType] = useState<
        | "user_connections"
        | "field_connections"
        | "irrigation_system_connections"
    >("user_connections");
    const [connectionPlatform, setConnectionPlatform] =
        useState<Platform | null>(null);
    const [pendingSystemIds, setPendingSystemIds] = useState<
        number[] | undefined
    >(undefined);
    const [pendingExportTargets, setPendingExportTargets] = useState<
        ExportTarget[]
    >([]);
    const [systemModalOpen, setSystemModalOpen] = useState(false);
    const [systemModalMode, setSystemModalMode] = useState<
        "download" | "export"
    >("download");
    const [exportUserConnectionId, setExportUserConnectionId] = useState<
        number | null
    >(null);
    const [selectedSystemIds, setSelectedSystemIds] = useState<number[]>([]);

    const [rxExported, setRxExported] = useState<boolean>(false);
    const exportRequest = useMutateRequest({
        method: "put",
        successMessage: t("success.exported_rx", { ns: "messages" }),
    });
    const buildDownloadUrl = (
        systemIds?: number[],
        acceptWarnings?: boolean,
    ) => {
        const params = new URLSearchParams();
        if (fileFormat) params.set("file_format", fileFormat);
        if (acceptWarnings) params.set("accept_warnings", "true");
        systemIds?.forEach((id) =>
            params.append("irrigation_system", String(id)),
        );
        const query = params.toString();
        return query
            ? `/prescriptions/${rx.id}/download/?${query}`
            : `/prescriptions/${rx.id}/download/`;
    };
    const systemName = (system: { id: number; name: string }) => {
        const fieldSystems = fieldQuery?.data?.irrigation_systems ?? [];
        const fieldIndex = fieldSystems.findIndex(
            (item) => item.id === system.id,
        );
        const listIndex = irrigationSystems.findIndex(
            (item) => item.id === system.id,
        );
        const number = (fieldIndex >= 0 ? fieldIndex : listIndex) + 1;
        return getIrrigationSystemDisplayName(
            system,
            t("irrigation_system.unnamed", {
                ns: "setup",
                number: number > 0 ? number : 1,
            }),
        );
    };
    const connectionForSystem = (
        systemId: number,
        userConnectionId: number | null,
    ) => {
        return irrigationSystemConnections.data?.find((connection) => {
            return (
                connection.irrigation_system === systemId &&
                platformFilter.includes(connection.user_connection.platform) &&
                (userConnectionId == null ||
                    connection.user_connection.id === userConnectionId)
            );
        });
    };
    const selectableSystemIds = (mode: "download" | "export", userConnectionId: number | null) => {
        if (mode === "download") {
            return irrigationSystems.map((system) => system.id);
        }
        return irrigationSystems
            .filter((system) => connectionForSystem(system.id, userConnectionId))
            .map((system) => system.id);
    };
    const openSystemModal = (
        mode: "download" | "export",
        userConnectionId?: number,
    ) => {
        const nextUserConnectionId = userConnectionId ?? null;
        setSystemModalMode(mode);
        setExportUserConnectionId(nextUserConnectionId);
        setSelectedSystemIds(selectableSystemIds(mode, nextUserConnectionId));
        setSystemModalOpen(true);
    };
    const prescriptionDownloadName = (systemIds?: number[]) => {
        if (!isFertigationRx) return rx.name;
        const ids = systemIds?.length
            ? systemIds
            : irrigationSystems.map((system) => system.id);
        if (ids.length === 0) return rx.name;
        if (ids.length !== 1) return `${rx.name}.zip`;
        const system = irrigationSystems.find((item) => item.id === ids[0]);
        const pump = isIrrigationSystem(system) ? system.pump : undefined;
        const extension =
            rx.rx_type === RxType.VRF &&
            pump?.controller_brand === ControllerBrand.uVrf
                ? "json"
                : "csv";
        const systemLabel = system ? systemName(system) : "";
        const base = systemLabel
            ? `${rx.name} ${systemLabel}`
            : rx.name;
        return `${base.replace(/[\\/]/g, "_")}.${extension}`;
    };
    const downloadSystems = async (systemIds: number[]) => {
        setAction("download");
        setPendingSystemIds(systemIds);
        await tryDownload(
            buildDownloadUrl(systemIds),
            prescriptionDownloadName(systemIds),
        );
    };
    const exportConnections = async (
        nextConnectionType:
            | "user_connections"
            | "field_connections"
            | "irrigation_system_connections",
        targets: ExportTarget[],
        acceptWarnings?: boolean,
        organizationId?: string,
    ) => {
        if (!targets.length) return;
        setConnectionType(nextConnectionType);
        setPendingExportTargets(targets);
        if (targets[0]) setConnectionId(targets[0].connectionId);
        try {
            for (const target of targets) {
                await exportRequest.mutateAsync({
                    url: `/${nextConnectionType}/${target.connectionId}/prescriptions/`,
                    data: {
                        prescription: rx.id,
                        ...(organizationId
                            ? { organization: organizationId }
                            : {}),
                        ...(target.irrigationSystemId
                            ? { irrigation_system: target.irrigationSystemId }
                            : {}),
                    },
                    params: {
                        accept_warnings: acceptWarnings,
                    },
                });
            }
            setRxExported(true);
        } catch (error) {
            handleExportError(error);
            setRxExported(false);
        }
    };
    const { isOpen, onOpenChange } = useDisclosure();
    return (
        <>
            <div className="flex flex-row w-full gap-2 items-center px-2 py-1">
                <Dropdown>
                    <DropdownTrigger>
                        <Button color="primary" className="w-1/2" size="sm">
                            <FontAwesomeIcon icon={faDownload} />
                            {t("download", { ns: "rx" })}
                        </Button>
                    </DropdownTrigger>
                    <DropdownMenu>
                        {rx.rx_type === RxType.VRF ? (
                            <DropdownItem
                                key="file-download"
                                onPress={async () => {
                                    setAction("download");
                                    setPendingSystemIds(undefined);
                                    if (needsSystemPicker) {
                                        openSystemModal("download");
                                        return;
                                    }
                                    await tryDownload(
                                        buildDownloadUrl(),
                                        prescriptionDownloadName(),
                                    );
                                }}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faFileZipper}
                                        className="text-primary"
                                    />
                                }
                            >
                                File
                            </DropdownItem>
                        ) : (
                            <DropdownItem
                                key="file-download"
                                onPress={() => {
                                    setFormatModalOpen(true);
                                    setAction("download");
                                }}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faFileZipper}
                                        className="text-primary"
                                    />
                                }
                            >
                                {t("file", { ns: "rx" })}
                            </DropdownItem>
                        )}
                        <DropdownItem
                            key="report-download"
                            startContent={
                                <FontAwesomeIcon
                                    icon={faFilePdf}
                                    className="text-primary"
                                />
                            }
                            onPress={async () => {
                                await downloadFile(
                                    `/prescriptions/${rx.id}/report/`,
                                    `${rx.name} Report`,
                                );
                            }}
                        >
                            {t("report", { ns: "rx" })}
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
                <Dropdown>
                    <DropdownTrigger>
                        <Button color="primary" className="w-1/2" size="sm">
                            <FontAwesomeIcon icon={faFileExport} />
                            {t("export_rx", { ns: "rx" })}
                        </Button>
                    </DropdownTrigger>
                    <DropdownMenu>
                        {exportOptions.map((option) => (
                            <DropdownItem
                                key={option.key}
                                onPress={() => {
                                    setAction("export");
                                    setConnectionPlatform(
                                        option.platform || null,
                                    );
                                    if (option.type === "more") {
                                        onOpenChange();
                                        return;
                                    }
                                    setConnectionType(option.type);
                                    if (
                                        needsSystemPicker &&
                                        option.type ===
                                            ConnectionType.IrrigationSystem &&
                                        option.userConnectionId
                                    ) {
                                        openSystemModal(
                                            "export",
                                            option.userConnectionId,
                                        );
                                        return;
                                    }
                                    if (option.connectionId) {
                                        setConnectionId(option.connectionId);
                                        exportConnections(option.type, [
                                            {
                                                connectionId:
                                                    option.connectionId,
                                            },
                                        ]);
                                    }
                                }}
                            >
                                {option.label}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            </div>

            <Modal
                isOpen={formatModalOpen}
                onClose={() => setFormatModalOpen(false)}
            >
                <ModalContent>
                    <ModalHeader>
                        <h2 className="text-lg font-bold">
                            {t("file_format", { ns: "rx" })}
                        </h2>
                    </ModalHeader>
                    <ModalBody>
                        <div className="flex flex-col gap-2">
                            <Autocomplete
                                label={t("file_format", { ns: "rx" })}
                                placeholder={t("optional", { ns: "rx" })}
                                defaultItems={fileFormatOptions}
                                onSelectionChange={(item) => {
                                    setFileFormat(item as RxFileFormat);
                                }}
                            >
                                {(item) => (
                                    <AutocompleteItem key={item.key}>
                                        {item.label}
                                    </AutocompleteItem>
                                )}
                            </Autocomplete>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            onPress={async () => {
                                if (needsSystemPicker) {
                                    setFormatModalOpen(false);
                                    openSystemModal("download");
                                    return;
                                }
                                setPendingSystemIds(undefined);
                                await tryDownload(
                                    buildDownloadUrl(),
                                    prescriptionDownloadName(),
                                );
                            }}
                        >
                            {t("download", { ns: "rx" })}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={warningModalOpen}
                onClose={() => setWarningModalOpen(false)}
            >
                <ModalContent>
                    <ModalBody>
                        {errors?.length > 0 && (
                            <div className="text-danger">
                                <h3 className="font-bold">
                                    {t("errors", { ns: "rx" })}:
                                </h3>
                                <ul className="list-disc pl-5">
                                    {errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {warnings?.length > 0 && (
                            <div className="text-warning">
                                <h3 className="font-bold">
                                    {t("warnings", { ns: "rx" })}:
                                </h3>
                                <ul className="list-disc pl-5">
                                    {warnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {errors.length === 0 && action === "download" && (
                            <Button
                                color="warning"
                                onPress={async () => {
                                    await downloadFile(
                                        buildDownloadUrl(
                                            pendingSystemIds,
                                            true,
                                        ),
                                        prescriptionDownloadName(
                                            pendingSystemIds,
                                        ),
                                    );
                                    setWarningModalOpen(false);
                                    setWarnings([]);
                                }}
                            >
                                {t("download_with_warnings", { ns: "rx" })}
                            </Button>
                        )}
                        {errors.length === 0 && action === "export" && (
                            <Button
                                color="warning"
                                onPress={() => {
                                    exportConnections(
                                        connectionType,
                                        pendingExportTargets.length
                                            ? pendingExportTargets
                                            : [{ connectionId }],
                                        true,
                                    );
                                    setWarningModalOpen(false);
                                    setWarnings([]);
                                }}
                            >
                                {t("export_with_warnings", { ns: "rx" })}
                            </Button>
                        )}
                        <Button onPress={() => setWarningModalOpen(false)}>
                            {t("close", { ns: "rx" })}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>
                        {t("export_to_a_connected_platform", { ns: "rx" })}
                    </ModalHeader>
                    <ModalBody>
                        <NewConnection
                            fieldId={rx.field}
                            rxId={rx.id}
                            platformFilter={
                                rx.rx_type === RxType.ZONE
                                    ? [Platform.JohnDeere, Platform.FieldView]
                                    : [Platform.FieldNET]
                            }
                            onFinish={(
                                connectionType: ConnectionType,
                                connectionId: number,
                                externalId?: string,
                            ) => {
                                exportConnections(
                                    connectionType,
                                    [{ connectionId }],
                                    false,
                                    externalId,
                                );
                                onOpenChange();
                            }}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={systemModalOpen}
                onClose={() => setSystemModalOpen(false)}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>
                        {t("irrigation_systems", { ns: "rx" })}
                    </ModalHeader>
                    <ModalBody>
                        <div className="flex flex-col gap-3">
                            <div>
                                <Button
                                    size="sm"
                                    variant="flat"
                                    isDisabled={
                                        selectableSystemIds(
                                            systemModalMode,
                                            exportUserConnectionId,
                                        ).length === 0
                                    }
                                    onPress={() => {
                                        setSelectedSystemIds(
                                            selectableSystemIds(
                                                systemModalMode,
                                                exportUserConnectionId,
                                            ),
                                        );
                                    }}
                                >
                                    {t("select_all", { ns: "rx" })}
                                </Button>
                            </div>
                            {irrigationSystems.map((system) => {
                                const selectable =
                                    systemModalMode === "download" ||
                                    !!connectionForSystem(
                                        system.id,
                                        exportUserConnectionId,
                                    );
                                return (
                                    <div
                                        key={system.id}
                                        className="flex flex-row items-center justify-between gap-2"
                                    >
                                        <Checkbox
                                            isSelected={selectedSystemIds.includes(
                                                system.id,
                                            )}
                                            isDisabled={!selectable}
                                            onValueChange={(checked) => {
                                                setSelectedSystemIds(
                                                    (current) => {
                                                        if (checked) {
                                                            return current.includes(
                                                                system.id,
                                                            )
                                                                ? current
                                                                : [
                                                                      ...current,
                                                                      system.id,
                                                                  ];
                                                        }
                                                        return current.filter(
                                                            (id) =>
                                                                id !==
                                                                system.id,
                                                        );
                                                    },
                                                );
                                            }}
                                        >
                                            {systemName(system)}
                                            {!selectable && (
                                                <span className="text-default-400">
                                                    {" "}
                                                    ({t("not_connected", {
                                                        ns: "rx",
                                                    })}
                                                    )
                                                </span>
                                            )}
                                        </Checkbox>
                                        <Button
                                            size="sm"
                                            variant="flat"
                                            aria-label={t("download", {
                                                ns: "rx",
                                            })}
                                            onPress={() => {
                                                downloadSystems([system.id]);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faDownload} />
                                            {t("download", { ns: "rx" })}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            isDisabled={selectedSystemIds.length === 0}
                            onPress={async () => {
                                if (systemModalMode === "download") {
                                    setSystemModalOpen(false);
                                    await downloadSystems(selectedSystemIds);
                                    return;
                                }
                                const targets = selectedSystemIds.flatMap(
                                    (systemId) => {
                                        const connection = connectionForSystem(
                                            systemId,
                                            exportUserConnectionId,
                                        );
                                        if (!connection) return [];
                                        return [
                                            {
                                                connectionId: connection.id,
                                                irrigationSystemId: systemId,
                                            },
                                        ];
                                    },
                                );
                                setSystemModalOpen(false);
                                await exportConnections(
                                    ConnectionType.IrrigationSystem,
                                    targets,
                                );
                            }}
                        >
                            {systemModalMode === "download"
                                ? t("download", { ns: "rx" })
                                : t("export_rx", { ns: "rx" })}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={rxExported} onClose={() => setRxExported(false)}>
                <ModalContent>
                    <ModalHeader className="text-success-500 flex flex-row items-center gap-2">
                        {" "}
                        Rx Exported
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </ModalHeader>
                    <ModalBody>
                        <p>
                            View the exported Rx in your connected{" "}
                            <Link
                                target="_blank"
                                href={
                                    connectionPlatform === Platform.FieldView
                                        ? "https://climate.com/static/field-view/#/data-inbox/view-files/imported-shapefiles"
                                        : connectionPlatform ===
                                            Platform.JohnDeere
                                          ? "https://files.deere.com/#/prescriptions"
                                          : ""
                                }
                            >
                                platform{" "}
                                <span className="text-primary inline-block ml-1">
                                    <FontAwesomeIcon
                                        icon={faArrowUpRightFromSquare}
                                    />
                                </span>
                            </Link>
                        </p>
                    </ModalBody>
                    <ModalFooter></ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

export default RxExport;
